/**
 * MeetingsTable — Shared filterable table shell for Upcoming and Past meetings.
 *
 * Handles: loading skeleton, empty state, count + clear-filters bar, column
 * search inputs, table container, and optional pagination. Consumers supply
 * column definitions and pre-filtered row data.
 */

import { ReactNode } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

// =============================================================================
// Types
// =============================================================================

export interface ColumnDef<T> {
  id: string;
  /** Header label — may include a sort button. */
  header: ReactNode;
  /** If provided, renders a search Input below the header label. */
  filterPlaceholder?: string;
  filterValue?: string;
  onFilterChange?: (value: string) => void;
  cell: (row: T) => ReactNode;
  /** Extra className on <TableHead>. */
  headClassName?: string;
  /** Extra className on <TableCell>. */
  cellClassName?: string;
}

interface PaginationProps {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

interface MeetingsTableProps<T> {
  columns: ColumnDef<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;

  isLoading?: boolean;

  /** Total count (may differ from rows.length when server-paginated). */
  total?: number;
  hasFilters?: boolean;
  onClearFilters?: () => void;

  /** Shown when there are no rows at all (before filtering). */
  emptyState?: ReactNode;
  /** Message shown inside the table body when filters match nothing. */
  noMatchMessage?: string;

  /** Rendered to the right of the count bar (e.g. "Schedule" button). */
  headerActions?: ReactNode;
  /** Extra items injected between the count/clear row and headerActions. */
  headerExtras?: ReactNode;

  pagination?: PaginationProps;
}

// =============================================================================
// MeetingsTable
// =============================================================================

export function MeetingsTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  isLoading = false,
  total,
  hasFilters = false,
  onClearFilters,
  emptyState,
  noMatchMessage = 'No meetings match the current filters.',
  headerActions,
  headerExtras,
  pagination,
}: MeetingsTableProps<T>) {
  const displayTotal = total ?? rows.length;
  const showCount = displayTotal > 0;

  return (
    <div className="space-y-4">
      {/* Count bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {showCount && (
            <p className="text-sm text-muted-foreground">
              {hasFilters
                ? `${rows.length} of ${displayTotal} meeting${displayTotal !== 1 ? 's' : ''}`
                : `${displayTotal} meeting${displayTotal !== 1 ? 's' : ''}`}
            </p>
          )}
          {hasFilters && onClearFilters && (
            <button
              onClick={onClearFilters}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="h-3 w-3" />
              Clear filters
            </button>
          )}
          {headerExtras}
        </div>
        {headerActions && <div>{headerActions}</div>}
      </div>

      {/* Table area */}
      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-12 rounded-lg bg-muted animate-pulse" />
          ))}
        </div>
      ) : displayTotal === 0 && !hasFilters && emptyState ? (
        emptyState
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                {columns.map((col) => (
                  <TableHead key={col.id} className={col.headClassName}>
                    {col.header}
                    {col.filterPlaceholder !== undefined && (
                      <Input
                        className="mt-1 h-7 text-xs font-normal"
                        placeholder={col.filterPlaceholder}
                        value={col.filterValue ?? ''}
                        onChange={(e) => col.onFilterChange?.(e.target.value)}
                      />
                    )}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="text-center text-sm text-muted-foreground py-8"
                  >
                    {noMatchMessage}
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow
                    key={rowKey(row)}
                    className={onRowClick ? 'cursor-pointer hover:bg-muted/50' : 'hover:bg-muted/50'}
                    onClick={() => onRowClick?.(row)}
                  >
                    {columns.map((col) => (
                      <TableCell key={col.id} className={col.cellClassName}>
                        {col.cell(row)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => pagination.onPageChange(Math.max(1, pagination.page - 1))}
              disabled={pagination.page === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => pagination.onPageChange(Math.min(pagination.totalPages, pagination.page + 1))}
              disabled={pagination.page === pagination.totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
